import { useEffect, useMemo, useState } from "react";
import "../../../../../css/CashflowModeller2/Inputs/Inputs.css";
import "../../../../../css/CashflowModeller2/Inputs/Input1.css";
import { useFormattedNumber } from "../../Hooks/UseFormattedNumber";

// keep digits, one dot, minus
const sanitizeNumeric = (s) => (s ?? "").replace(/[^0-9.-]/g, "");

// format integer part with commas, keep decimals/trailing "." exactly
const formatWithCommasKeepDecimals = (raw) => {
  if (raw === "" || raw === "-" || raw === "." || raw === "-.") return raw;
  const m = raw.match(/^(-?)(\d*)(\.?)(\d*)$/);
  if (!m) return raw;
  const [, sign, intPart = "", dot = "", decPart = ""] = m;
  const intWithCommas =
    intPart === "" ? "" : intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${intWithCommas}${dot}${decPart}`;
};

// used only for blur display (a “pretty” number)
const formatNumber = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const n = Number(String(val).replace(/,/g, ""));
  if (!isFinite(n)) return "";
  return n.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const Input1 = ({
  isDisabled,
  type = "text", // "number", "text", "customDate", "percent" (optional)
  label,
  value,
  currency = false,
  per = false, // enables % formatting
  onChange,
  readOnly,
  isError,
}) => {
  const [status, setStatus] = useState("default");
  const isFocused = status === "selected";

  // Treat type="percent" as per={true}
  const isPercent = per || type === "percent";
  const numericType = type === "percent" ? "number" : type;

  const { rawValue, formattedValue, handleChange } = useFormattedNumber(
    value ?? "",
    numericType
  );

  // BLUR-display value (commas via Number(); adds £/%)
  const prettyValue = useMemo(() => {
    if (numericType !== "number") return value ?? "";
    const base = rawValue === "" ? value : rawValue;
    if (base === "" || base === null || base === undefined) return "";
    const asNum = formatNumber(base);
    if (!asNum) return "";
    if (currency) return `£${asNum}`;
    if (isPercent) return `${asNum}%`;
    return asNum;
  }, [numericType, currency, isPercent, rawValue, value]);

  const [displayValue, setDisplayValue] = useState("");

  // Sync external changes → display (don’t clobber while typing)
  useEffect(() => {
    if (numericType !== "number") {
      setDisplayValue(value ?? "");
      return;
    }
    if (!isFocused) {
      setDisplayValue(prettyValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedValue, prettyValue, value, numericType, isFocused]);

  const handleLocalChange = (e) => {
    const val = e.target.value;

    if (numericType === "number") {
      // Strip commas/symbols to a raw numeric string
      const raw = sanitizeNumeric(val);

      // allow one dot and optional leading minus
      const numericRegex = /^-?\d*\.?\d*$/;
      if (!numericRegex.test(raw)) return;

      // Show commas while typing BUT keep decimals/trailing dot
      setDisplayValue(formatWithCommasKeepDecimals(raw));

      // Update internal hook + parent with the raw numeric string
      handleChange({ target: { value: raw } });
      onChange?.(raw === "" ? "" : raw);
      return;
    }

    setDisplayValue(val);
    onChange?.(val);
  };

  const handleFocus = () => {
    if (readOnly) return;
    setStatus("selected");

    if (numericType === "number") {
      // show raw digits on focus (no £/%)
      setDisplayValue(sanitizeNumeric(String(rawValue || value || "")));
    }
  };

  const handleBlur = () => {
    setStatus("default");
    if (numericType === "number") {
      // reapply pretty blur display (adds £/%)
      setDisplayValue(prettyValue);
    }
  };

  const numberPlaceholder = currency
    ? "£25,000"
    : isPercent
    ? "7%"
    : "18,000.00";

  return (
    <div className={`${label ? "input-label-container" : ""}`}>
      {label && <div className="input1 Label">{label}</div>}

      <div
        className={`input-container ${isDisabled ? "disabled" : ""} ${status} ${
          readOnly ? "readOnly" : ""
        } ${isError ? "error" : ""}`}
      >
        <input
          type={
            numericType === "number"
              ? "text"
              : type === "customDate"
              ? "number"
              : "text"
          }
          inputMode={numericType === "number" ? "decimal" : "text"}
          // keep pattern loose; we'll validate ourselves
          pattern={numericType === "number" ? "-?\\d*\\.?\\d*" : undefined}
          value={displayValue ?? ""}
          onChange={handleLocalChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="input-field Input"
          placeholder={
            numericType === "number"
              ? numberPlaceholder
              : type === "customDate"
              ? "YYYY"
              : "Aviva"
          }
          disabled={isDisabled}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

export default Input1;
